import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Contact, Person, SchemaOptions } from '@crmsy/shared';
import { EMPTY_FILTERS, filtersFromSearch, filtersToSearch, toggleInList, toggleWindow, type Filters, type ListFilterKey } from './filters.js';
import { groupingFromSearch, groupingToSearch, type GroupKey } from './grouping.js';
import type { KeyMap } from './keyboard.js';
import { navigate, useLocation } from './router.js';
import { Picker, type PickerOption } from '../components/Picker.jsx';

const PICKER_PROPERTY: Record<ListFilterKey, string> = { stage: 'Stage', nextAction: 'Next Action', status: 'Status', owner: 'Owner' };
const PICKER_TITLE: Record<ListFilterKey, string> = { stage: 'Stage', nextAction: 'Next action', status: 'Status', owner: 'Owner' };
const HOTKEY_TO_PICKER: Record<string, ListFilterKey> = { s: 'stage', a: 'nextAction', t: 'status', o: 'owner' };

export interface ListFiltersOptions {
  search: string;
  setSearch: (q: string) => void;
  contacts: Contact[] | undefined;
  people: Person[];
  schema: SchemaOptions;
  /** Follow-ups (§6.2): "mine" is on unless the URL says `all`. */
  defaultMine?: boolean;
  /** D-38: grouping when the URL has no `group=`; Follow-ups default to the due buckets. */
  defaultGroups?: GroupKey[];
  /** Remembers whether the grouping bar is open, per view and device. */
  view: 'table' | 'follow-ups';
}

const NO_GROUPS: GroupKey[] = [];
const barKey = (view: string) => `crmsy.groupBar:${view}`;

/** Filter state in the URL (§6.3), the filter pickers and their list-context keys (§7). Shared by Table and Follow-ups. */
export function useListFilters(o: ListFiltersOptions) {
  const loc = useLocation();
  const filters = useMemo(() => {
    const f = { ...filtersFromSearch(loc.search), q: o.search };
    if (o.defaultMine && !loc.search.has('all') && !f.mine) f.mine = true;
    return f;
  }, [loc.search, o.search, o.defaultMine]);
  const defaultGroups = o.defaultGroups ?? NO_GROUPS;
  const groups = useMemo(() => groupingFromSearch(loc.search, defaultGroups), [loc.search, defaultGroups]);
  const write = useCallback(
    (f: Filters, g: GroupKey[]) => {
      o.setSearch(f.q);
      const sp = filtersToSearch({ ...f, q: '' });
      if (o.defaultMine && !f.mine) sp.set('all', '');
      groupingToSearch(sp, g, defaultGroups);
      navigate(loc.path, sp, true);
    },
    [loc.path, o.setSearch, o.defaultMine, defaultGroups],
  );
  const setFilters = useCallback((f: Filters) => write(f, groups), [write, groups]);
  const setGroups = useCallback((g: GroupKey[]) => write(filters, g), [write, filters]);

  const [barOpen, setBarOpen] = useState(() => {
    try {
      return localStorage.getItem(barKey(o.view)) === '1';
    } catch {
      return false;
    }
  });
  const toggleBar = useCallback(() => {
    setBarOpen((v) => {
      try {
        localStorage.setItem(barKey(o.view), v ? '0' : '1');
      } catch {
        /* private mode */
      }
      return !v;
    });
  }, [o.view]);

  const [picker, setPicker] = useState<{ key: ListFilterKey; anchor: { x: number; y: number } | null } | null>(null);
  const openPicker = useCallback((key: ListFilterKey, anchor: { x: number; y: number } | null = null) => {
    if (!anchor) {
      const chip = document.querySelector<HTMLElement>(`[data-filter-key="${key}"]`);
      const r = chip?.getBoundingClientRect();
      if (r) anchor = { x: r.left, y: r.bottom + 6 };
    }
    setPicker((p) => (p?.key === key ? null : { key, anchor }));
  }, []);

  // While a picker is open, its own letter closes it (toggle) even though the input has focus.
  useEffect(() => {
    if (!picker) return;
    const onKey = (e: KeyboardEvent) => {
      const k = HOTKEY_TO_PICKER[e.key];
      const input = e.target as HTMLInputElement;
      if (k === picker.key && input.tagName === 'INPUT' && input.value === '') {
        e.preventDefault();
        setPicker(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [picker]);

  const pickerOptions = useMemo<PickerOption[]>(() => {
    if (!picker) return [];
    const counts = new Map<string, number>();
    for (const c of o.contacts ?? []) {
      if (c.archived) continue;
      const vals = picker.key === 'owner' ? c.owner : [c[picker.key] ?? ''];
      for (const v of vals) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    if (picker.key === 'owner') {
      return o.people.map((p) => ({ value: p.id, label: p.name, count: counts.get(p.id) ?? 0 })).sort((a, b) => b.count - a.count);
    }
    const opts = (o.schema[PICKER_PROPERTY[picker.key]] ?? []).map((x) => ({ value: x.name, label: x.name, color: x.color, count: counts.get(x.name) ?? 0 }));
    const seen = new Set(opts.map((x) => x.value));
    for (const [v, n] of counts) if (!seen.has(v)) opts.push({ value: v, label: v || '(empty)', color: 'default', count: n });
    return opts;
  }, [picker, o.contacts, o.people, o.schema]);

  const keys: KeyMap = {
    s: () => openPicker('stage'),
    a: () => openPicker('nextAction'),
    t: () => openPicker('status'),
    o: () => openPicker('owner'),
    i: () => setFilters({ ...filters, mine: !filters.mine }),
    d: () => setFilters(toggleWindow(filters, 'due')),
    w: () => setFilters(toggleWindow(filters, 'week')),
    m: () => setFilters(toggleWindow(filters, 'month')),
    x: () => setFilters({ ...EMPTY_FILTERS }),
    g: toggleBar,
  };

  /** Esc: close the picker, else clear the search; returns false when nothing was open. */
  const escape = (): boolean => {
    if (picker) {
      setPicker(null);
      return true;
    }
    if (o.search) {
      o.setSearch('');
      return true;
    }
    return false;
  };

  const pickerElement = picker ? (
    <Picker
      title={PICKER_TITLE[picker.key]}
      options={pickerOptions}
      selected={filters[picker.key]}
      multi
      onToggle={(v) => setFilters({ ...filters, [picker.key]: toggleInList(filters[picker.key], v) })}
      onClose={() => setPicker(null)}
      anchor={picker.anchor}
    />
  ) : null;

  return { filters, setFilters, groups, setGroups, barOpen, toggleBar, openPicker, pickerElement, keys, escape, pickerOpen: !!picker };
}
